import React, { useEffect, useState, useMemo } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_BRANCHES,
  GET_STOCK_COUNT,
  START_STOCK_COUNT,
  SUBMIT_STOCK_COUNT,
  CANCEL_STOCK_COUNT,
} from "./queries";
import {
  StockCountItem,
  CATEGORY_OPTIONS,
  CATEGORY_LABELS,
  VARIANCE_REASON_LABELS,
} from "./models";

interface CountedItem {
  item_id: number;
  counted_qty: string;
  variance_reason: string;
  variance_notes: string;
}

const StockCountForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const resumeId = params.get("resume");

  // Setup state
  const [branchId, setBranchId] = useState("");
  const [countType, setCountType] = useState("full");
  const [category, setCategory] = useState("");

  // Counting state
  const [activeCount, setActiveCount] = useState<any>(null);
  const [countedItems, setCountedItems] = useState<Record<number, CountedItem>>({});
  const [submitting, setSubmitting] = useState(false);

  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });
  const [fetchCount, { loading: loadingCount }] = useLazyQuery(GET_STOCK_COUNT, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.getStockCount) {
        setActiveCount(data.getStockCount);
        // Pre-populate counted items
        const items: Record<number, CountedItem> = {};
        for (const item of data.getStockCount.items || []) {
          items[item.id] = {
            item_id: parseInt(item.id),
            counted_qty: item.counted_qty != null ? String(item.counted_qty) : "",
            variance_reason: item.variance_reason || "",
            variance_notes: item.variance_notes || "",
          };
        }
        setCountedItems(items);
      }
    },
  });

  const [startCount, { loading: starting }] = useMutation(START_STOCK_COUNT);
  const [submitCount] = useMutation(SUBMIT_STOCK_COUNT);
  const [cancelCount] = useMutation(CANCEL_STOCK_COUNT);

  useEffect(() => {
    fetchBranches();
    if (resumeId) {
      fetchCount({ variables: { id: resumeId } });
    }
    //eslint-disable-next-line
  }, [resumeId]);

  const handleStart = async () => {
    if (!branchId) {
      toast.error(lang("Selecciona una sucursal", "Select a branch"));
      return;
    }
    try {
      const variables: any = {
        branch_id: parseInt(branchId),
        count_type: countType,
      };
      if (countType === "category" && category) {
        variables.category = category;
      }
      const { data } = await startCount({ variables });
      if (data?.startStockCount) {
        setActiveCount(data.startStockCount);
        // Initialize counted items
        const items: Record<number, CountedItem> = {};
        for (const item of data.startStockCount.items || []) {
          items[item.id] = {
            item_id: parseInt(item.id),
            counted_qty: "",
            variance_reason: "",
            variance_notes: "",
          };
        }
        setCountedItems(items);
        toast.success(lang("Conteo iniciado", "Count started"));
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleCountedChange = (itemId: number, field: string, value: string) => {
    setCountedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!activeCount) return;

    // Validate all items have counted qty
    const items = Object.values(countedItems);
    const incomplete = items.filter((i) => i.counted_qty === "");
    if (incomplete.length > 0) {
      toast.error(
        lang(
          `Faltan ${incomplete.length} items por contar`,
          `${incomplete.length} items still need counting`
        )
      );
      return;
    }

    setSubmitting(true);
    try {
      const submitItems = items.map((i) => ({
        item_id: i.item_id,
        counted_qty: parseFloat(i.counted_qty),
        variance_reason: i.variance_reason || undefined,
        variance_notes: i.variance_notes || undefined,
      }));

      await submitCount({
        variables: {
          count_id: parseInt(activeCount.id),
          items: submitItems,
        },
      });

      toast.success(lang("Conteo enviado exitosamente", "Count submitted successfully"));
      history.push(`/inventory/counts/${activeCount.id}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!activeCount) return;
    if (
      !window.confirm(
        lang("¿Cancelar este conteo?", "Cancel this count?")
      )
    ) {
      return;
    }
    try {
      await cancelCount({ variables: { id: activeCount.id } });
      toast.success(lang("Conteo cancelado", "Count cancelled"));
      history.push("/inventory/counts");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Group items by category
  const groupedItems = useMemo(() => {
    if (!activeCount?.items) return {};
    const groups: Record<string, StockCountItem[]> = {};
    for (const item of activeCount.items) {
      const cat = item.ingredient?.category || "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return groups;
  }, [activeCount]);

  if (loadingCount) return <LoaderDots fullSize />;

  // Stage 1: Setup
  if (!activeCount) {
    return (
      <>
        <p>
          <Link to="/inventory/counts">
            <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
            {lang("Conteos", "Counts")}
          </Link>
        </p>
        <h1 className="mb-4">{lang("Nuevo Conteo", "New Stock Count")}</h1>

        <div className="row">
          <div className="col-lg-6">
            <div className="material_card p-4">
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Sucursal", "Branch")} *
                </label>
                <select
                  className="custom-select"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">
                    {lang("Seleccionar sucursal...", "Select branch...")}
                  </option>
                  {branchData?.getBranches?.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Tipo de Conteo", "Count Type")}
                </label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="countType"
                      value="full"
                      checked={countType === "full"}
                      onChange={(e) => setCountType(e.target.value)}
                    />
                    <label className="form-check-label">
                      {lang("Completo", "Full")}
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="countType"
                      value="category"
                      checked={countType === "category"}
                      onChange={(e) => setCountType(e.target.value)}
                    />
                    <label className="form-check-label">
                      {lang("Por Categoría", "By Category")}
                    </label>
                  </div>
                </div>
              </div>

              {countType === "category" && (
                <div className="form-group">
                  <label className="font-weight-bold">
                    {lang("Categoría", "Category")}
                  </label>
                  <select
                    className="custom-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">
                      {lang("Seleccionar...", "Select...")}
                    </option>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {lang(cat.label, cat.labelEn)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                className="btn btn-primary btn-block mt-3"
                onClick={handleStart}
                disabled={starting || !branchId}
              >
                {starting ? (
                  <span className="spinner-border spinner-border-sm mr-2" />
                ) : (
                  <i className="fas fa-clipboard-check mr-2"></i>
                )}
                {lang("Iniciar Conteo", "Start Count")}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Stage 2: Counting
  return (
    <>
      <p>
        <Link to="/inventory/counts">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Conteos", "Counts")}
        </Link>
      </p>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">
          {lang("Conteo", "Count")} #{activeCount.id} —{" "}
          {activeCount.branch?.name || ""}
        </h1>
        <div>
          <button
            className="btn btn-success mr-2"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm mr-2" />
            ) : (
              <i className="fas fa-check mr-2"></i>
            )}
            {lang("Enviar Conteo", "Submit Count")}
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={handleCancel}
          >
            <i className="fa fa-times mr-1"></i>
            {lang("Cancelar", "Cancel")}
          </button>
        </div>
      </div>

      <p className="text-muted mb-3">
        {lang(
          `${activeCount.items?.length || 0} ingredientes por contar`,
          `${activeCount.items?.length || 0} ingredients to count`
        )}
      </p>

      {Object.entries(groupedItems).map(([cat, items]) => (
        <div key={cat} className="material_card p-3 mb-3">
          <h5 className="mb-3">
            <span className="badge badge-secondary mr-2">
              {lang(
                CATEGORY_LABELS[cat]?.label || cat,
                CATEGORY_LABELS[cat]?.labelEn || cat
              )}
            </span>
            {items.length} {lang("items", "items")}
          </h5>
          <div className="table-responsive">
            <table className="table table-sm vertical_middle">
              <thead className="thead-light">
                <tr>
                  <th>{lang("Ingrediente", "Ingredient")}</th>
                  <th>{lang("Ubicación", "Location")}</th>
                  <th className="text-center">
                    {lang("Esperado", "Expected")}
                  </th>
                  <th className="text-center" style={{ width: 120 }}>
                    {lang("Contado", "Counted")}
                  </th>
                  <th className="text-center">
                    {lang("Varianza", "Variance")}
                  </th>
                  <th style={{ width: 180 }}>
                    {lang("Razón", "Reason")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item: StockCountItem) => {
                  const counted = countedItems[parseInt(item.id)];
                  const countedVal = counted?.counted_qty
                    ? parseFloat(counted.counted_qty)
                    : null;
                  const expectedVal = parseFloat(String(item.expected_qty)) || 0;
                  const variance =
                    countedVal != null ? countedVal - expectedVal : null;
                  const hasVariance = variance != null && variance !== 0;

                  return (
                    <tr
                      key={item.id}
                      className={
                        hasVariance
                          ? variance! < 0
                            ? "table-danger"
                            : "table-warning"
                          : ""
                      }
                    >
                      <td className="font-weight-bold">
                        {item.ingredient?.name || `#${item.ingredient_id}`}
                      </td>
                      <td>
                        <small className="text-muted">
                          {item.location || "kitchen"}
                        </small>
                      </td>
                      <td className="text-center">
                        {Number(expectedVal).toFixed(2)} {item.unit}
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm text-center"
                          value={counted?.counted_qty || ""}
                          onChange={(e) =>
                            handleCountedChange(
                              parseInt(item.id),
                              "counted_qty",
                              e.target.value
                            )
                          }
                          placeholder="0.00"
                        />
                      </td>
                      <td className="text-center">
                        {variance != null ? (
                          <span
                            className={`font-weight-bold ${
                              variance < 0
                                ? "text-danger"
                                : variance > 0
                                ? "text-warning"
                                : "text-success"
                            }`}
                          >
                            {variance > 0 ? "+" : ""}
                            {Number(variance).toFixed(2)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {hasVariance && (
                          <select
                            className="custom-select custom-select-sm"
                            value={counted?.variance_reason || ""}
                            onChange={(e) =>
                              handleCountedChange(
                                parseInt(item.id),
                                "variance_reason",
                                e.target.value
                              )
                            }
                          >
                            <option value="">—</option>
                            {Object.entries(VARIANCE_REASON_LABELS).map(
                              ([key, val]) => (
                                <option key={key} value={key}>
                                  {lang(val.label, val.labelEn)}
                                </option>
                              )
                            )}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
};

export default StockCountForm;
